<template>
  <section
    ref="sectionEl"
    id="benefits"
    class="relative py-28 bg-gray-50 dark:bg-dark overflow-hidden transition-colors duration-300"
    :class="{ 'is-visible': isVisible }"
  >
    <div class="section-glow-center"></div>

    <div class="container mx-auto px-4 relative z-10">
      <div class="max-w-3xl mx-auto text-center mb-20 anim-el d1">
        <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">{{ t('benefits.title') }}</p>
        <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1] mb-6 transition-colors duration-300">
          {{ t('benefits.subtitle') }}
        </h2>
        <p class="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
          {{ t('benefits.description') }}
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto mb-16">
        <div
          v-for="(stat, index) in stats"
          :key="index"
          class="rounded-2xl bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] p-7 hover:border-blue-300 dark:hover:border-blue-500/20 hover:shadow-sm transition-all duration-300 text-center group anim-el"
          :class="'d' + (index + 2)"
        >
          <div class="w-11 h-11 rounded-xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
            <component :is="stat.icon" class="h-5 w-5 text-blue-500" />
          </div>
          <div class="text-3xl font-bold text-gray-900 dark:text-white mb-1 transition-colors duration-300">{{ stat.value }}</div>
          <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">{{ stat.label }}</p>
          <p class="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">{{ stat.description }}</p>
        </div>
      </div>

      <div class="text-center anim-el d6">
        <router-link
          to="/contact"
          class="inline-flex items-center h-12 px-7 bg-gray-900 dark:bg-white text-white dark:text-dark font-semibold text-[15px] rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors group"
        >
          {{ t('benefits.cta') }}
          <ArrowRight class="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </router-link>
        <p class="mt-4 text-sm text-gray-400 dark:text-gray-600">{{ t('benefits.ctaSubtext') }}</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { Zap, Clock, Target, Wallet, ArrowRight } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const sectionEl = ref(null)
const isVisible = ref(false)
let observer = null

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) isVisible.value = true
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' })
  if (sectionEl.value) observer.observe(sectionEl.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const stats = computed(() => [
  { icon: Zap, value: t('benefits.stats.speed.value'), label: t('benefits.stats.speed.label'), description: t('benefits.stats.speed.description') },
  { icon: Clock, value: t('benefits.stats.availability.value'), label: t('benefits.stats.availability.label'), description: t('benefits.stats.availability.description') },
  { icon: Target, value: t('benefits.stats.accuracy.value'), label: t('benefits.stats.accuracy.label'), description: t('benefits.stats.accuracy.description') },
  { icon: Wallet, value: t('benefits.stats.cost.value'), label: t('benefits.stats.cost.label'), description: t('benefits.stats.cost.description') }
])
</script>

<style scoped>
.section-glow-center {
  position: absolute;
  top: 30%;
  left: 50%;
  transform: translateX(-50%);
  width: 50%;
  height: 40%;
  background: radial-gradient(ellipse at center, rgba(59, 130, 246, 0.05) 0%, transparent 60%);
  pointer-events: none;
}

.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el { opacity: 1; transform: translateY(0); }
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.12s; }
.d3 { transition-delay: 0.20s; }
.d4 { transition-delay: 0.28s; }
.d5 { transition-delay: 0.36s; }
.d6 { transition-delay: 0.44s; }
</style>
